import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Card, Button, Alert, Spin, Space, Segmented, Switch, Table, Tabs, Tooltip, Tag,
  Drawer, Statistic, Row, Col, Empty,
} from 'antd';
import { ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  ResponsiveContainer, ReferenceLine, Legend,
} from 'recharts';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import PageLayout from '../components/layout/PageLayout';
import api from '../libs/api';

/**
 * Weather-cluster map of monitored systems.
 *
 * Drawn here rather than embedding the pipeline's own 16.8 MB Folium page,
 * because that page cannot be filtered or restyled: everything sits on one flat
 * layer, all 10,361 assigned systems are the same green, and the viewport is
 * stretched across the Pacific by a handful of bad coordinates. The backend
 * extracts the records (~1.3 MB of JSON) and this renders them.
 *
 * Colour encodes DISTANCE TO CLUSTER CENTRE, not cluster identity. There are 204
 * clusters, far past the point where a hue per cluster means anything, and
 * distance is the signal that matters: it is how far a system sits from the
 * weather station standing in for it. State is a filter, not a colour.
 */

// Sequential single hue, light -> dark. Ordinal steps from the validated blue
// ramp; the light end is step 250, the lightest that still clears the surface.
// Breaks follow the measured distribution: p10 2.0, median 6.3, p90 13.1 km.
const BANDS = [
  { max: 3, colour: '#86b6ef', label: '≤ 3 km' },
  { max: 6, colour: '#5598e7', label: '3 – 6 km' },
  { max: 10, colour: '#2a78d6', label: '6 – 10 km' },
  { max: 15, colour: '#1c5cab', label: '10 – 15 km' },
  { max: Infinity, colour: '#104281', label: '> 15 km' },
];
// Reserved status colour, never a series hue. Carries a distinct square marker
// and a legend entry, because at 2.57:1 on this surface it must not be the only
// thing distinguishing these points.
const UNASSIGNED = '#ec835a';
const CENTRE_INK = '#52514e';
const SURFACE = '#fcfcfb';

const bandOf = (km) => (km == null ? BANDS[0] : BANDS.find((b) => km <= b.max));

// WA is left out deliberately: it holds two systems, both unassigned to any
// cluster, so a WA view would be an empty map with two orphans on it. They are
// reported in the banner rather than dropped silently.
const STATE_ORDER = ['SA', 'NSW', 'QLD', 'VIC', 'ACT', 'NT', 'TAS'];
const EXCLUDED_STATES = new Set(['WA', 'OUT']);
/**
 * Himawari-9, as NASA GIBS serves it.
 *
 * The geostationary satellite that watches Australia, re-imaged every ten
 * minutes. GIBS tiles it into Web Mercator and asks for no key, which is the
 * whole reason it is usable here — the three basemaps this page went through
 * before all failed on licence rather than on pixels.
 *
 * Two layers because one satellite cannot do both jobs. The visible band
 * shows cloud the way an eye would and sees nothing at night; the clean
 * infrared band works around the clock by reading temperature instead, which
 * is coarser and less obvious to look at. Which one is right depends on
 * whether the sun is up over Australia, so the page picks rather than asking.
 *
 * `maxNativeZoom` is the part that has to be right. The satellite resolves
 * about a kilometre at nadir, so GIBS simply has no tiles past these levels
 * and answers 404. Without the cap Leaflet requests them anyway and paints
 * nothing — the same silent blank the basemap gave before it was bounded.
 */
const GIBS = 'https://gibs.earthdata.nasa.gov/wmts/epsg3857/best';
const CLOUD_LAYERS = {
  visible: {
    id: 'Himawari_AHI_Band3_Red_Visible_1km',
    maxNativeZoom: 7,
    label: 'visible',
    className: 'cloud-overlay-visible',
    opacity: 0.9,
  },
  infrared: {
    id: 'Himawari_AHI_Band13_Clean_Infrared',
    maxNativeZoom: 6,
    label: 'infrared',
    className: 'cloud-overlay-infrared',
    // The chroma key has already removed everything that is not cloud, so
    // what remains can be drawn at full strength.
    opacity: 1,
  },
};

/**
 * The most recent ten-minute frame that is likely to have landed.
 *
 * GIBS publishes on ten-minute boundaries and runs some way behind real time
 * while the scan is processed. Asking for the current minute returns nothing,
 * so this steps back far enough to be safe and then rounds down onto a
 * boundary — anything else is not a frame that exists.
 */
const CLOUD_LAG_MINUTES = 40;
function latestCloudFrame() {
  const t = new Date(Date.now() - CLOUD_LAG_MINUTES * 60 * 1000);
  t.setUTCMinutes(Math.floor(t.getUTCMinutes() / 10) * 10, 0, 0);
  return t;
}

/**
 * Where the sun is, rather than what the clock says.
 *
 * This decided the band on a fixed UTC window before, and the window was
 * four hours wider than the visible band is actually usable: measured off
 * the frames themselves, brightness over Adelaide ran 34 at local noon and
 * 19 by late afternoon, and `screen` cannot lift 19 off the basemap. Clouds
 * that were plainly there at lunchtime had simply stopped appearing.
 *
 * A fixed window cannot be right in any case, because it does not move with
 * the seasons: the same hour is full sun in December and dusk in June.
 */
function solarElevation(date, latDeg, lonDeg) {
  const rad = Math.PI / 180;
  const d = (date.getTime() / 86400000) + 2440587.5 - 2451545.0;   // days since J2000
  const g = (357.529 + (0.98560028 * d)) * rad;                    // mean anomaly
  const q = 280.459 + (0.98564736 * d);                            // mean longitude
  const L = (q + (1.915 * Math.sin(g)) + (0.020 * Math.sin(2 * g))) * rad;
  const e = (23.439 - (0.00000036 * d)) * rad;                     // obliquity
  const dec = Math.asin(Math.sin(e) * Math.sin(L));                // declination
  const ra = Math.atan2(Math.cos(e) * Math.sin(L), Math.cos(L));   // right ascension
  const gmst = (18.697374558 + (24.06570982441908 * d)) % 24;      // sidereal, hours
  const ha = (((gmst * 15) + lonDeg) * rad) - ra;                  // local hour angle
  const lat = latDeg * rad;
  return Math.asin((Math.sin(lat) * Math.sin(dec))
    + (Math.cos(lat) * Math.cos(dec) * Math.cos(ha))) / rad;
}

/**
 * Below this the visible band is too dim to be worth showing: sunlight on the
 * ground falls off as the sine of this angle, so 15 degrees is already down to
 * a quarter of what the same cloud reflects overhead. It works out at about
 * ten and a half hours of visible band in October and under seven in June,
 * which is the seasonal movement the old fixed window could not follow.
 */
const VISIBLE_MIN_ELEVATION = 15;

/**
 * Reference point for that decision.
 *
 * The continent spans three hours of sun, so no single point is right for all
 * of it. This is the middle of where the systems actually are — the great
 * majority sit in South Australia and New South Wales — rather than the
 * geographic centre of the land mass.
 */
const SUN_REF = [-33.5, 141.0];

function cloudBandFor(date) {
  return solarElevation(date, SUN_REF[0], SUN_REF[1]) > VISIBLE_MIN_ELEVATION
    ? 'visible' : 'infrared';
}

/**
 * How hard to push the visible band, given how low the sun is.
 *
 * Reflected light goes with the sine of the elevation, so a frame taken at 25
 * degrees carries roughly half the light of one at noon through no fault of
 * the cloud in it. This divides that back out, normalised to a high sun and
 * capped so that a nearly dark frame is not amplified into noise.
 */
function cloudGainFor(date) {
  const el = solarElevation(date, SUN_REF[0], SUN_REF[1]);
  if (el <= 0) return 1;
  const gain = Math.sin(60 * (Math.PI / 180)) / Math.sin(el * (Math.PI / 180));
  return Math.round(Math.min(2.2, Math.max(1, gain)) * 100) / 100;
}

const AU_CENTRE = [-30, 135];

// Each state opens on its capital rather than framed to fit every point in it.
// Fitting the bounds put the whole state on screen, which is the same problem
// the original map had at national scale: the systems are concentrated in the
// metro area, so the useful detail was squeezed into a corner while empty
// hinterland took the rest of the canvas.
const CAPITAL = {
  SA: [-34.9285, 138.6007],    // Adelaide
  NSW: [-33.8688, 151.2093],   // Sydney
  QLD: [-27.4698, 153.0251],   // Brisbane
  VIC: [-37.8136, 144.9631],   // Melbourne
  ACT: [-35.2809, 149.1300],   // Canberra
  NT: [-12.4634, 130.8456],    // Darwin
  TAS: [-42.8821, 147.3272],   // Hobart
};
// Levels closer than the zoom that would have fitted the whole state. Derived
// from the fit rather than hard-coded per state, so a state whose footprint
// changes still opens at a sensible scale.
//
// Three suits the big states, which fit at z5-6 and so open around z8-9 — metro
// scale. The ACT already fits at z9 because it is small, so the same +3 put it at
// z12, street level, with its 101 systems spread past the edges. It takes no
// adjustment: its own fit is already the right frame.
/**
 * Everything on this page's live panel is AEST, and it says so everywhere.
 *
 * Three sources with three clocks meet here — weatherapi reports in the
 * station's own zone, AEMO settles in AEST with no daylight saving, and the
 * reader's browser is on whatever it is on. The backend converts all of it to
 * AEST; the label is not decoration, it is the thing that makes the numbers
 * comparable.
 */
const TZ_LABEL = 'AEST (UTC+10)';

/**
 * The whole AEST day, as one axis every chart shares.
 *
 * Minutes from AEST midnight rather than a category of time labels. The three
 * series are sampled quite differently — hourly weather, five-minute prices,
 * and a station west of the AEST meridian reporting on the half hour — so as
 * categories each chart got an axis of its own and the columns did not line up
 * with each other at all. A numeric axis with a fixed 0–1440 domain makes
 * midday the same horizontal position in all three whatever the sampling.
 */
const DAY_MINUTES = 1440;
const AXIS_TICKS = [0, 180, 360, 540, 720, 900, 1080, 1260, DAY_MINUTES];
const minsOf = (aest) => {
  if (!aest) return null;
  const h = Number(String(aest).slice(11, 13));
  const m = Number(String(aest).slice(14, 16));
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
};
const minsLabel = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

const fmtTemp = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}°C`);
const fmtGhi = (v) => (v == null ? '—' : `${Math.round(v)} W/m²`);
const fmtPrice = (v) => (v == null ? '—' : `${v < 0 ? '-$' : '$'}${Math.abs(v).toFixed(2)}`);
const hhmm = (aest) => (aest ? String(aest).slice(11, 16) : '');

// Distinct from the map's distance ramp, which is a sequential blue: these are
// three separate measures, each its own chart, so each gets one hue of its own.
const TEMP_INK = '#c2582d';
const GHI_INK = '#c9a227';
const PRICE_INK = '#2a78d6';

/**
 * The station's own zone, from whichever read has arrived.
 *
 * Both endpoints report it; the popup's read lands first, so the banner can
 * name the conversion before the day's series is back.
 */
const tzOf = (now, detail) => {
  const st = (now && now.data && now.data.station) || (detail && detail.data && detail.data.station);
  if (!st || !st.tzId) return null;
  return `${st.tzId}${st.localtime ? `, local ${String(st.localtime).slice(11)}` : ''}`;
};

const ZOOM_IN_LEVELS = 3;
const ZOOM_IN_BY = { ACT: 0 };
const MAX_ZOOM = 18;

/**
 * The live block inside a station's popup.
 *
 * Built as an HTML string because Leaflet popups are strings, not React. The
 * spinner is a CSS animation for the same reason — there is no component here
 * to render one, and a popup that sits blank for the two seconds three APIs
 * take reads as a popup that is broken.
 */
function conditionsHtml(cluster, cond) {
  if (!cond || cond.loading) {
    return `<div style="margin-top:8px;padding-top:8px;border-top:1px solid #eee;color:#888">`
      + `<span class="mos-spin"></span> reading weather and price…`
      + `</div>`;
  }
  if (cond.error) {
    return `<div style="margin-top:8px;padding-top:8px;border-top:1px solid #eee;color:#a32f2a">`
      + `${cond.error}</div>`;
  }
  const d = cond.data;
  const c = d.current || {};
  const icon = c.condition && c.condition.icon
    ? `<img src="${c.condition.icon}" alt="" style="width:32px;height:32px;vertical-align:middle;margin-right:4px">`
    : '';
  const weatherLine = d.currentError
    ? `<div style="color:#a32f2a">weather unavailable — ${d.currentError}</div>`
    : `<div>${icon}<b style="font-size:14px">${fmtTemp(c.tempC)}</b>`
      + `<span style="color:#555"> · ${c.condition ? c.condition.text : '—'}</span>`
      + `<span style="color:#888;font-size:11px"> · UV ${c.uv == null ? '—' : c.uv}`
      + ` · cloud ${c.cloud == null ? '—' : `${c.cloud}%`}</span></div>`;
  const priceLine = d.price && d.price.region
    ? `<b>${fmtPrice(d.price.latest ? d.price.latest.rrp : null)}</b>`
      + `<span style="color:#888"> /MWh · ${d.price.region}`
      + `${d.price.latest ? ` · ${hhmm(d.price.latest.aest)}` : ''}</span>`
    : `<span style="color:#888">${(d.price && d.price.reason) || 'no spot price'}</span>`;

  // No irradiance here on purpose: nothing measures it, and the only source is
  // the hourly forecast, which is two more API calls. It appears in the detail
  // panel, where it has been paid for.
  return `<div style="margin-top:8px;padding-top:8px;border-top:1px solid #eee;line-height:1.7">`
    + weatherLine
    + `<div>${priceLine}</div>`
    + `<div style="color:#888;font-size:11px">${TZ_LABEL}`
    + `${d.station && d.station.tzId ? ` · station reports in ${d.station.tzId}` : ''}</div>`
    + `<div style="margin-top:6px">`
    + `<a href="#" data-detail="${cluster.replace(/"/g, '&quot;')}" `
    + `style="color:#2a78d6;font-weight:600;text-decoration:none">Detail → temperature, irradiance &amp; price</a></div>`
    + `</div>`;
}

export default function MapOfSystems() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stateFilter, setStateFilter] = useState('SA');
  const [showRings, setShowRings] = useState(true);
  const [showLines, setShowLines] = useState(true);
  // Off by default: it is context, and it covers the only thing this page is
  // really about.
  const [showClouds, setShowClouds] = useState(false);
  const [cloudFrame, setCloudFrame] = useState(null);
  const cloudLayerRef = useRef(null);

  const [selected, setSelected] = useState(null);   // cluster name, or null
  // cluster name -> { loading, error, data }. Cached per station so reopening a
  // popup does not spend three API calls again.
  const [conditions, setConditions] = useState({});
  const [detailFor, setDetailFor] = useState(null);  // cluster name, or null
  // The day's series, fetched only when the detail panel is opened. Separate
  // from `conditions` because the two are paid for at different moments.
  const [days, setDays] = useState({});

  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const layerRef = useRef(null);
  // Layers are kept so a selection can restyle them in place. Rebuilding the
  // group on every click would mean re-creating 10,371 markers and re-binding
  // as many tooltips, which is the expensive part of the draw.
  const marksRef = useRef({ sites: [], lines: [], rings: [], stations: [] });
  /**
   * The popup is a detached DOM string, so it cannot read React state.
   *
   * These two refs are how the marker layer — built once, outside the render —
   * reaches the current conditions and asks for a fetch. Without them the
   * popup's content would be frozen at whatever state existed when the layer
   * was drawn.
   */
  const conditionsRef = useRef({});
  const askRef = useRef(() => {});
  // Read by the draw effect so it can apply the live selection WITHOUT depending
  // on it — depending on it would rebuild every marker on each click, which is
  // exactly what keeping the layers around is meant to avoid.
  const selectedRef = useRef(null);

  const load = useCallback(async (refresh = false) => {
    setLoading(true); setError(null);
    try {
      const r = await api.get(`/api/map-of-systems/points${refresh ? '?refresh=1' : ''}`, { timeout: 300000 });
      setData(r.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load the cluster data');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Default to whichever state actually holds the most systems rather than
  // assuming; the mix shifts as the fleet grows.
  useEffect(() => {
    if (!data) return;
    const best = STATE_ORDER
      .filter((s) => data.byState[s])
      .sort((a, b) => data.byState[b] - data.byState[a])[0];
    if (best) setStateFilter(best);
  }, [data]);

  const visible = useMemo(() => {
    if (!data) return { points: [], centres: [] };
    if (stateFilter === 'ALL') {
      // Outliers are upstream errors, not places; including them would stretch
      // the bounds from Adelaide to Los Angeles.
      return {
        points: data.points.filter((p) => !EXCLUDED_STATES.has(p.state)),
        centres: data.centres.filter((c) => !EXCLUDED_STATES.has(c.state)),
      };
    }
    return {
      points: data.points.filter((p) => p.state === stateFilter),
      centres: data.centres.filter((c) => c.state === stateFilter),
    };
  }, [data, stateFilter]);

  /**
   * Apply the current selection to the layers already on the map.
   *
   * Selecting emphasises by dimming everything else rather than by recolouring
   * the selection: the dots keep their distance-band colour, which is the whole
   * point of them, and the surrounding clutter recedes.
   */
  const applySelection = useCallback((cluster) => {
    const { sites, lines, rings, stations } = marksRef.current;
    const dim = !!cluster;
    for (const m of sites) {
      const on = !dim || m.cluster === cluster;
      m.layer.setStyle({
        fillOpacity: on ? 0.95 : 0.1,
        opacity: on ? 1 : 0.1,
        weight: on && dim ? 1.5 : 1,
      });
    }
    for (const m of lines) {
      const on = !dim || m.cluster === cluster;
      m.layer.setStyle({ opacity: on ? (dim ? 0.55 : 0.3) : 0.04 });
    }
    for (const m of rings) {
      const on = !dim || m.cluster === cluster;
      // The selected cluster's ring IS the boundary being asked for, so it comes
      // forward while the rest of the catchment mesh drops away.
      m.layer.setStyle({
        opacity: on ? (dim ? 0.85 : 0.25) : 0.05,
        weight: on && dim ? 2 : 1,
        fillOpacity: on && dim ? 0.07 : 0.03,
      });
    }
    for (const m of stations) {
      const on = !dim || m.cluster === cluster;
      m.layer.setStyle({ fillOpacity: on ? 1 : 0.2, opacity: on ? 1 : 0.2 });
    }
  }, []);

  const clustersInView = useMemo(() => {
    if (!data) return [];
    return stateFilter === 'ALL'
      ? data.clusters.filter((c) => !EXCLUDED_STATES.has(c.state))
      : data.clusters.filter((c) => c.state === stateFilter);
  }, [data, stateFilter]);

  // ---- selection styling ---------------------------------------------------
  useEffect(() => { conditionsRef.current = conditions; }, [conditions]);

  /**
   * Weather and price for one station.
   *
   * Cached per station and never re-fetched while a request for it is already
   * out: opening a popup, closing it and opening it again is one click each
   * way, and each fetch costs three weather API calls against a metered quota.
   */
  const askConditions = useCallback(async (cluster, lat, lon, state) => {
    const held = conditionsRef.current[cluster];
    if (held && (held.loading || held.data)) return held;

    /**
     * The ref is written here as well as the state, and the result is returned.
     *
     * The popup repaints from what this resolves to, not from the ref, because
     * the ref is refreshed by an effect that runs after the next render — later
     * than the promise's own continuation. Reading it there returned the
     * `loading: true` value that had just been set, so the popup repainted its
     * own spinner and did so forever. The backend was answering in under a
     * second the whole time.
     */
    const put = (next) => {
      conditionsRef.current = { ...conditionsRef.current, [cluster]: next };
      setConditions((prev) => ({ ...prev, [cluster]: next }));
      return next;
    };

    put({ loading: true });
    try {
      const r = await api.get('/api/map-of-systems/station-now', {
        params: { lat, lon, state },
        timeout: 30000,
      });
      return put({ loading: false, data: r.data.data });
    } catch (err) {
      return put({
        loading: false,
        error: err.response?.data?.message || 'Could not read the conditions',
      });
    }
  }, []);

  /**
   * The day's temperature, irradiance and price — only once asked for.
   *
   * Three reads where the popup needs two, and two of them are the metered
   * weather endpoints, so nothing fetches this until the detail panel opens.
   */
  const askDay = useCallback(async (cluster, lat, lon, state) => {
    setDays((prev) => {
      if (prev[cluster] && (prev[cluster].loading || prev[cluster].data)) return prev;
      return { ...prev, [cluster]: { loading: true } };
    });
    try {
      const r = await api.get('/api/map-of-systems/station-day', {
        params: { lat, lon, state },
        timeout: 60000,
      });
      setDays((prev) => ({ ...prev, [cluster]: { loading: false, data: r.data.data } }));
    } catch (err) {
      setDays((prev) => ({
        ...prev,
        [cluster]: {
          loading: false,
          error: err.response?.data?.message || 'Could not read the day',
        },
      }));
    }
  }, []);
  useEffect(() => { askRef.current = askConditions; }, [askConditions]);

  // `now` is already loaded — the popup fetched it — so the panel opens showing
  // real figures while the day's series is still in flight.
  const now = detailFor ? conditions[detailFor] : null;
  const detail = detailFor ? days[detailFor] : null;

  /**
   * Opening the panel is what pays for the day.
   *
   * Keyed on the station rather than fired from the click handler so that a
   * panel reopened for a station already read costs nothing, and so the fetch
   * cannot be missed when the panel is opened some other way.
   */
  useEffect(() => {
    if (!detailFor) return;
    // centres carries exactly what the day read needs: cluster, lat, lon, state.
    const st = (data?.centres || []).find((c) => c.cluster === detailFor);
    if (!st) return;
    askDay(detailFor, st.lat, st.lon, st.state);
  }, [detailFor, data, askDay]);

  /**
   * Chart rows, split into a past key and a future key.
   *
   * The last elapsed point is written into BOTH keys on purpose: two Recharts
   * lines cannot share a segment, so without that overlap the solid and dashed
   * halves would be separated by a visible gap exactly at "now".
   */
  const nowCur = (now && now.data && now.data.current) || {};
  const nowPrice = (now && now.data && now.data.price) || {};

  /**
   * Rows for the three charts, split exactly at "now".
   *
   * The solid and dashed halves need a point in common or they are drawn with a
   * gap between them. Reusing the last elapsed reading for that was wrong in a
   * way the chart made obvious: the hourly points sit on the half hour at a
   * station west of the AEST meridian, so with the clock at 16:20 the join was
   * the 15:30 reading and the dashed line appeared to start fifty minutes
   * before the marker said the future did.
   *
   * The join is now a synthetic point at the current minute, interpolated
   * between the readings either side of it. Both halves own it, so they meet on
   * the marker and nowhere else. Measured data for the current hour was never
   * missing — this was only where the two lines were stitched.
   */
  const chartRows = useMemo(() => {
    const d = detail && detail.data;   // the day read, not the popup read
    if (!d) return { temp: [], ghi: [], price: [] };
    const nowM = minsOf(d.nowAest);

    /**
     * @param pts [{ m, t, v }] in any order
     * Points at or before now carry `past`, at or after now carry `future`, and
     * the boundary carries both.
     */
    const split = (pts) => {
      const rows = pts
        .filter((x) => x.m != null && x.v != null && Number.isFinite(x.v))
        .sort((a, b) => a.m - b.m);
      const out = [];
      let joined = false;
      rows.forEach((cur, i) => {
        const prev = rows[i - 1];
        // Strict on prev: a reading sitting exactly on the current minute is
        // already the boundary — it carries both keys — and inserting beside
        // it would put two points on the same minute.
        if (!joined && nowM != null && prev && prev.m < nowM && cur.m > nowM) {
          // Straight-line interpolation. Over one hourly step of temperature or
          // irradiance that is well inside the precision either figure carries,
          // and it buys a join that lands on the minute rather than up to an
          // hour early.
          const f = (nowM - prev.m) / (cur.m - prev.m);
          const v = prev.v + (cur.v - prev.v) * f;
          out.push({ m: nowM, t: minsLabel(nowM), past: v, future: v });
          joined = true;
        }
        out.push({
          m: cur.m,
          t: cur.t,
          past: nowM == null || cur.m <= nowM ? cur.v : null,
          future: nowM != null && cur.m >= nowM ? cur.v : null,
        });
      });
      return out;
    };

    const weather = (valueOf) => split(d.series.map((x) => ({
      m: minsOf(x.aest),
      t: hhmm(x.aest),
      v: valueOf(x),
    })));

    /**
     * Price gets the same treatment even though only its past half exists.
     *
     * TRADINGPRICE is the settled record and carries no forecast, but a
     * forecast price is coming: shaping it now means the dashed half appears
     * the moment the data does, joined on the same minute as the weather,
     * rather than the chart needing to be rebuilt around a second series.
     * `rrpForecast` is read if the backend ever sends it.
     */
    const price = split((d.price.rows || []).map((r) => ({
      m: minsOf(r.aest),
      t: hhmm(r.aest),
      v: r.rrpForecast != null ? r.rrpForecast : r.rrp,
    })));

    return {
      // Measured where it exists, forecast where it does not — for an hour that
      // has already happened the measurement is the better number.
      temp: weather((x) => (x.tempActualC != null && x.past ? x.tempActualC : x.tempC)),
      ghi: weather((x) => x.ghi),
      price,
    };
  }, [detail]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    /**
     * "Detail →" is inside a Leaflet popup, whose DOM is replaced on every
     * repaint, so the handler is delegated to the container once rather than
     * re-attached to a link that keeps being thrown away.
     */
    containerRef.current.addEventListener('click', (ev) => {
      const link = ev.target.closest && ev.target.closest('[data-detail]');
      if (!link) return;
      ev.preventDefault();
      setDetailFor(link.getAttribute('data-detail'));
    });

    /**
     * Where the basemap actually has tiles.
     *
     * The service advertises detail levels 0 to 23 over the whole world. Asked
     * for them, it does not have them: every level past 16 answers 404, and so
     * does open ocean from about level 8 — the Tasman and the Coral Sea are
     * both empty by level 10. The cache was built for Australia, and the
     * metadata describes the projection rather than the holdings.
     *
     * Leaflet draws nothing for a 404 and says nothing about it, so a view that
     * drifts off the continent turns silently grey and stays grey. Bounding the
     * map keeps it over the tiles that exist — no loss here, since every system
     * on this page is Australian.
     */
    const AU_BOUNDS = L.latLngBounds([-44.5, 112.0], [-9.0, 154.5]);
    /**
     * What the tile layer may ask for, as opposed to where the map may go.
     *
     * These were the same box, and that was wrong: the continent's own
     * outline became a hard edge in the picture. Brisbane sits at 153°E and
     * Sydney at 151°E, so a limit at 154.5 cuts the sea off a few centimetres
     * past the coast and leaves the right of the screen blank — with the
     * cloud layer, which has no such limit, still drawn over the gap.
     *
     * The map's own maxBounds is what keeps the view over Australia; this
     * only needs to be wide enough that the viewport is always filled inside
     * it, so it matches that padded box rather than the tight one.
     */
    const TILE_BOUNDS = AU_BOUNDS.pad(0.35);

    const map = L.map(containerRef.current, {
      preferCanvas: true,
      zoomControl: true,
      maxBounds: AU_BOUNDS.pad(0.35),
      // Soft rather than rigid: dragging past the edge is resisted and springs
      // back, instead of the map feeling stuck.
      maxBoundsViscosity: 0.5,
    }).setView(AU_CENTRE, 4);
    /**
     * Geoscience Australia's National Base Map.
     *
     * The third basemap this page has had, and the first whose licence
     * actually covers what we are doing with it:
     *
     *   OpenStreetMap's own servers are volunteer-run and their usage policy
     *   does not extend to an application like this. They answered with a 403
     *   image reading "Access blocked", which Leaflet tiled across the map.
     *
     *   CARTO serve a real map with "API KEY REQUIRED" stamped diagonally
     *   across every tile — so comparing two tiles byte for byte said they were
     *   fine, and only looking at one showed they were not.
     *
     *   Esri's grey canvas is clean and needs no key, but their terms are
     *   categorical rather than numeric: use with Esri software, and "if you do
     *   not have Esri software, you must purchase an ArcGIS Online
     *   subscription." No quota to stay under — we simply are not entitled.
     *
     * This one is Commonwealth of Australia, released under CC BY 4.0. Open
     * licence, no key, no subscription, and it covers every state we operate
     * in because that is the entire point of it.
     *
     * It is a full-colour topographic map, which would compete with the only
     * thing on this page anyone is looking at, so it is desaturated in CSS
     * down to something close to a grey canvas. Muting the tiles we are allowed
     * to use beats using tiles that already look right.
     *
     * That licence is why a broken tile grid was worked around rather than
     * swapped out: Esri's grey canvas would have been a two-line change, and
     * we still are not entitled to it.
     */
    /**
     * Asked for by bounding box rather than by tile number, because the tile
     * numbers stopped meaning what Leaflet thinks they mean.
     *
     * An ArcGIS cache declares the top-left corner its grid counts from. A
     * standard web-mercator grid - the one Leaflet's {z}/{x}/{y} assumes -
     * starts at y = 20,037,508.34. This service now reports 30,241,100, ten
     * thousand kilometres further north, so row 1,236 at zoom 11 is no longer
     * Adelaide: it is 47.7N, 138.6E, in the Russian Far East, and that is what
     * the map drew - correct markers floating over Khabarovsk Krai.
     *
     * The offset is 0.2546 x 2^z tiles, which is never a whole number, so the
     * grids cannot be brought back into line by shifting the row. The service
     * also advertises a standard GoogleMapsCompatible grid over WMTS, but
     * ignores the parameter: both grids return byte-identical tiles.
     *
     * So this asks the export endpoint for the exact bounding box each
     * standard tile covers. The server renders it on demand - around 0.4s a
     * tile, a screenful in about a second, and the browser caches them - and
     * the result lines up with Leaflet's grid by construction, because the
     * box is computed from that grid.
     */
    const MERC_HALF = 20037508.342789244;
    const GA_EXPORT = 'https://services.ga.gov.au/gis/rest/services/NationalBaseMap/MapServer/export';
    const GaBaseLayer = L.TileLayer.extend({
      // coords.z, not _getZoomForUrl(): the box has to describe the grid these
      // coordinates came from, which is the native zoom Leaflet clamped to.
      getTileUrl(coords) {
        const span = (MERC_HALF * 2) / Math.pow(2, coords.z);
        const xmin = -MERC_HALF + (coords.x * span);
        const ymax = MERC_HALF - (coords.y * span);
        const bbox = [xmin, ymax - span, xmin + span, ymax].join(',');
        return `${GA_EXPORT}?bbox=${bbox}&bboxSR=3857&imageSR=3857`
          + '&size=256,256&format=png&transparent=false&f=image';
      },
    });
    new GaBaseLayer('', {
      maxZoom: 19,
      // The map itself runs out around 16 - past that the renderer returns a
      // near-empty image rather than more detail, so Leaflet upscales the
      // deepest real tiles instead. Blurry beats blank on one street.
      maxNativeZoom: 16,
      // Bounded so the far ocean is not requested at depth, where there is
      // nothing to draw - but wide enough that the edge never shows inside
      // the viewport.
      bounds: TILE_BOUNDS,
      className: 'basemap-muted',
      attribution: '&copy; Geoscience Australia (CC BY 4.0), &copy; OpenStreetMap contributors',
    }).addTo(map);
    mapRef.current = map;
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !data) return;
    if (layerRef.current) { map.removeLayer(layerRef.current); layerRef.current = null; }

    // Canvas, not SVG: 4,500 markers as DOM nodes makes panning crawl.
    const renderer = L.canvas({ padding: 0.3 });
    const group = L.layerGroup();
    const centreOf = new Map(data.clusters.filter((c) => c.lat != null).map((c) => [c.cluster, c]));
    const marks = { sites: [], lines: [], rings: [], stations: [] };

    if (showRings) {
      for (const c of visible.centres) {
        // The 20 km catchment, recessive — context for the dots, not a mark of
        // its own.
        marks.rings.push({
          cluster: c.cluster,
          layer: L.circle([c.lat, c.lon], {
            radius: (data.radiusKm || 20) * 1000,
            renderer, color: CENTRE_INK, weight: 1, opacity: 0.25,
            fillColor: CENTRE_INK, fillOpacity: 0.03, interactive: false,
          }).addTo(group),
        });
      }
    }

    // Spokes first, so every mark sits on top of them. Thin and recessive on
    // purpose: the line says which station a system answers to, while the dot's
    // colour says how far away that station is — the line should not compete
    // with the value it is context for.
    if (showLines) {
      for (const p of visible.points) {
        const c = p.cluster && centreOf.get(p.cluster);
        if (!c) continue;
        marks.lines.push({
          cluster: p.cluster,
          layer: L.polyline([[p.lat, p.lon], [c.lat, c.lon]], {
            renderer, color: CENTRE_INK, weight: 0.6, opacity: 0.3, interactive: false,
          }).addTo(group),
        });
      }
    }

    for (const p of visible.points) {
      if (p.unassigned) {
        // Same dot as an assigned system, in the reserved status colour. It was a
        // rectangle sized in degrees, which meant it grew on screen as the map
        // zoomed in — at metro zoom the square was several times the size of the
        // dots around it. A circleMarker's radius is in pixels, so it stays put.
        //
        // Identity now rests on hue plus the legend and the hover label rather
        // than on shape. That is sound here: orange against the blue ramp clears
        // both separation floors (normal-vision dE 15.7, CVD 13.9), and there are
        // 128 of these against 10,371 — they read as the exception either way.
        marks.sites.push({ cluster: null, layer: L.circleMarker([p.lat, p.lon], {
          renderer, radius: 3.5, color: SURFACE, weight: 1,
          fillColor: UNASSIGNED, fillOpacity: 0.95,
        }).addTo(group).bindTooltip(
          `<b>${p.sn || 'unknown'}</b>${p.nmi ? `<br>NMI ${p.nmi}` : ''}<br>not assigned to a cluster`,
          { direction: 'top' },
        ) });
        continue;
      }
      const band = bandOf(p.km);
      // A surface-coloured ring, not a darker one: where systems sit on top of
      // each other the gap between them is what separates the marks, and a
      // 1,798-system cluster otherwise reads as a single solid mass.
      marks.sites.push({ cluster: p.cluster, layer: L.circleMarker([p.lat, p.lon], {
        renderer, radius: 3.5, color: SURFACE, weight: 1,
        fillColor: band.colour, fillOpacity: 0.95,
        // As with the stations: without this the click also reaches the map,
        // whose handler clears the selection, so selecting would undo itself.
        bubblingMouseEvents: false,
      }).addTo(group).bindTooltip(
        `<b>${p.sn || 'unknown'}</b>${p.nmi ? `<br>NMI ${p.nmi}` : ''}`
        + `<br>${p.cluster} · ${p.km} km from centre`,
        { direction: 'top' },
      ).on('click', () => setSelected(p.cluster)) });
    }

    // Centres last so they sit above the dots they belong to. Small: the station
    // is a reference point, not the subject, and at the old size it hid the very
    // systems clustered tightly around it. It stays legible against them by
    // colour and by its surface ring, not by area.
    //
    // Drawn from the cluster records because those are the only ones that carry
    // a name — the centre markers in the artefact have none, which is why they
    // used to be anonymous dots.
    const named = new Set();
    for (const c of clustersInView) {
      if (c.lat == null) continue;
      named.add(`${c.lat},${c.lon}`);
      const station = L.circleMarker([c.lat, c.lon], {
        renderer, radius: 3, color: SURFACE, weight: 1.5,
        fillColor: CENTRE_INK, fillOpacity: 1,
        // Without this the click also reaches the map, whose handler clears the
        // selection — so selecting would undo itself.
        bubblingMouseEvents: false,
      });
      marks.stations.push({ cluster: c.cluster, layer: station });
      // The unchanging half of the popup. The live half is appended when the
      // popup opens and replaced again when the fetch lands.
      const head = `<div style="font-size:12px;line-height:1.6">`
        + `<b style="font-size:13px">${c.cluster}</b><br>`
        + `<span style="color:#888">weather station${c.clusterId != null ? ` · id ${c.clusterId}` : ''}</span><br>`
        + `<b>${c.systems.toLocaleString()}</b> systems under this station<br>`
        + `mean ${c.kmMean} km · furthest ${c.kmMax} km<br>`
        + `<span style="color:#888">${c.lat.toFixed(3)}, ${c.lon.toFixed(3)} · ${c.state}</span>`;
      station
        .addTo(group)
        .bindTooltip(`<b>${c.cluster}</b> · ${c.systems.toLocaleString()} systems`, { direction: 'top' })
        .bindPopup(`${head}</div>`, { minWidth: 240 })
        .on('popupopen', (e) => {
          const paint = (cond) => e.popup.setContent(
            `${head}${conditionsHtml(c.cluster, cond)}</div>`,
          );
          // What is already known, or the spinner.
          paint(conditionsRef.current[c.cluster]);
          // Then whatever the fetch resolves to — passed in, never re-read from
          // the ref, which is a render behind at this point.
          askRef.current(c.cluster, c.lat, c.lon, c.state)
            .then((cond) => paint(cond))
            .catch(() => paint({ loading: false, error: 'Could not read the conditions' }));
        })
        // A station toggles: clicking the one already isolated means "collapse
        // this". A site only ever selects — see the site handler above. Clicking
        // a dot inside the cluster you are inspecting means "tell me about this
        // one", and dumping the view back to everything there would fight the
        // user rather than help them.
        .on('click', () => setSelected((cur) => (cur === c.cluster ? null : c.cluster)));
    }
    // Two stations in the current artefact have no systems assigned. They are
    // real locations, so they are drawn and labelled as such rather than
    // quietly dropped for lacking a name.
    for (const c of visible.centres) {
      if (named.has(`${c.lat},${c.lon}`)) continue;
      const empty = L.circleMarker([c.lat, c.lon], {
        renderer, radius: 3, color: SURFACE, weight: 1.5,
        fillColor: CENTRE_INK, fillOpacity: 0.55, bubblingMouseEvents: false,
      });
      marks.stations.push({ cluster: c.cluster || '__none__', layer: empty });
      empty
        .addTo(group)
        .bindTooltip('weather station · no systems assigned', { direction: 'top' })
        .bindPopup('<div style="font-size:12px">weather station<br><b>0</b> systems under this station</div>');
    }

    group.addTo(map);
    layerRef.current = group;
    marksRef.current = marks;
    // A redraw (a toggle, a state change) must not silently drop the selection
    // that is on screen.
    applySelection(selectedRef.current);
  }, [data, visible, clustersInView, showRings, showLines, applySelection]);

  // Restyle in place when the selection changes, rather than rebuilding.
  useEffect(() => {
    selectedRef.current = selected;
    applySelection(selected);
  }, [selected, applySelection]);

  // Clicking the map background clears the selection.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;
    const clear = () => setSelected(null);
    map.on('click', clear);
    return () => map.off('click', clear);
  }, []);

  // Changing state drops a selection that belongs to the state being left.
  useEffect(() => { setSelected(null); }, [stateFilter]);

  // Camera moves only when the state changes, never when a layer toggles — the
  // draw effect above re-runs on every switch, and re-framing there discarded
  // whatever the user had panned or zoomed to.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !data) return;
    const pts = visible.points.map((p) => [p.lat, p.lon]);
    if (!pts.length) { map.setView(AU_CENTRE, 4); return; }

    const bounds = L.latLngBounds(pts).pad(0.08);
    const capital = CAPITAL[stateFilter];
    if (!capital) { map.fitBounds(bounds); return; }   // the all-Australia view

    // getBoundsZoom answers "what zoom would show all of this", which is the
    // baseline the three levels are counted from.
    const fitted = map.getBoundsZoom(bounds);
    const closer = ZOOM_IN_BY[stateFilter] ?? ZOOM_IN_LEVELS;
    map.setView(capital, Math.min(fitted + closer, MAX_ZOOM));
    // Deliberately not depending on `visible`: it is derived from these two, and
    // listing it would re-fire the camera on every toggle — the thing this
    // effect exists to avoid.
  }, [data, stateFilter]);

  /**
   * The cloud overlay, added and removed with the switch.
   *
   * Kept out of the marker effect deliberately: that one rebuilds on every
   * filter change, and a tile layer that is torn down and recreated each time
   * flickers and refetches for no reason. This owns the layer and nothing else
   * touches it.
   */
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;

    if (!showClouds) {
      if (cloudLayerRef.current) {
        map.removeLayer(cloudLayerRef.current);
        cloudLayerRef.current = null;
      }
      setCloudFrame(null);
      return undefined;
    }

    const add = () => {
      const when = latestCloudFrame();
      const band = CLOUD_LAYERS[cloudBandFor(when)];
      const stamp = when.toISOString().replace(/\.\d{3}Z$/, 'Z');
      if (cloudLayerRef.current) map.removeLayer(cloudLayerRef.current);
      const layer = L.tileLayer(
        `${GIBS}/${band.id}/default/${stamp}`
        + `/GoogleMapsCompatible_Level${band.maxNativeZoom}/{z}/{y}/{x}.png`,
        {
          maxZoom: 19,
          maxNativeZoom: band.maxNativeZoom,
          // Set per band: what keeps clear sky out of the way is the blend
          // or the chroma key, not transparency. Dimming the whole layer
          // would only dim the cloud with it.
          opacity: band.opacity,
          /**
           * Above the basemap, below everything that matters.
           *
           * Leaflet keeps tiles in their own pane, well under the markers, so
           * the systems stay on top without being asked. What does need asking
           * is the order *within* that pane: a basemap is opaque, so a cloud
           * layer behind it is invisible — which is exactly what bringToBack()
           * did here, hiding the thing it had just loaded.
           */
          zIndex: 2,
          // GIBS answers 404 where a frame has no data for that tile. Leaflet
          // draws nothing and says nothing, so an empty square would read as
          // clear sky; a transparent pixel at least keeps it uniform.
          errorTileUrl: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
          className: band.className,
          attribution: 'Cloud: Himawari-9 via NASA GIBS',
        },
      );
      layer.addTo(map);
      // The visible band's CSS reads this; the infrared band's rendering is a
      // chroma key on a temperature image, where brightness means nothing.
      const gain = band.label === 'visible' ? cloudGainFor(when) : 1;
      const container = layer.getContainer();
      if (container) container.style.setProperty('--cloud-gain', gain);
      cloudLayerRef.current = layer;
      setCloudFrame({ at: when, band: band.label, gain });
    };

    add();
    // A new frame every ten minutes; re-request on the same cadence so a map
    // left open does not keep showing the hour it was opened.
    const timer = setInterval(add, 10 * 60 * 1000);
    return () => {
      clearInterval(timer);
      if (cloudLayerRef.current) {
        map.removeLayer(cloudLayerRef.current);
        cloudLayerRef.current = null;
      }
    };
  }, [showClouds]);

  /**
   * Keep Leaflet's idea of the container's size honest.
   *
   * Leaflet measures the container once, on creation, and thereafter only
   * re-measures on a window resize. Everything else that changes this map's
   * size is invisible to it: switching to the Clusters tab and back, which
   * antd does by setting display:none on the pane; collapsing the sidebar,
   * which changes the width with no window event at all.
   *
   * When the cached size is stale Leaflet keeps computing against it. The
   * pixel origin is wrong, so it requests tiles for a viewport that is not the
   * one on screen and paints them offset — a block of map floating in grey,
   * usually nowhere near where the camera was pointed. Zooming or panning then
   * makes it worse rather than better, because each move is measured from the
   * same wrong frame.
   *
   * A ResizeObserver catches every one of those causes, including ones nobody
   * has thought of yet. The zero check matters: a hidden pane reports 0x0, and
   * invalidating against that would replace a stale size with a useless one.
   */
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    let frame = null;
    const ro = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect;
      if (!box || box.width === 0 || box.height === 0) return;
      // Coalesce: a tab switch fires several times as layout settles, and
      // invalidateSize is a full re-measure and tile reload each time.
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = null;
        if (mapRef.current) mapRef.current.invalidateSize();
      });
    });
    ro.observe(el);
    return () => { if (frame) cancelAnimationFrame(frame); ro.disconnect(); };
  }, []);

  const stateOptions = useMemo(() => {
    if (!data) return [{ label: 'All', value: 'ALL' }];
    const inAu = STATE_ORDER.filter((s) => data.byState[s]);
    return [
      ...inAu.map((s) => ({ label: `${s} (${data.byState[s].toLocaleString()})`, value: s })),
      { label: `All AU (${inAu.reduce((a, s) => a + data.byState[s], 0).toLocaleString()})`, value: 'ALL' },
    ];
  }, [data]);



  return (
    <PageLayout>
      {/*
        * Keys the infrared overlay on colour rather than brightness.
        *
        * Alpha is -2R + G + B, which is zero for anything neutral — black,
        * mid-grey and white alike — and rises the further a pixel leans blue,
        * cyan or green. That is exactly the split the infrared ramp draws:
        * warm ground in greys, cold cloud tops in colour.
        *
        * It lives here because `filter: url(#id)` resolves against the
        * document, so the definition has to be on the page, not in the
        * stylesheet.
        */}
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
        <defs>
          {/*
            * An unsharp mask over the upscaled tiles.
            *
            * The satellite resolves about a kilometre and the map is read at
            * ten times that, so every tile arrives having been interpolated up
            * and comes out soft. This puts the edges back — not the detail,
            * which is gone and cannot be recovered, but the contrast at the
            * boundaries that makes a cloud edge read as an edge.
            *
            * Cosmetic and deliberately so: nothing here adds information, it
            * only stops the information that is there looking like fog. The
            * kernel sums to one so overall brightness is unchanged, and
            * preserveAlpha keeps it off the transparency the chroma key is
            * about to set.
            */}
          <filter id="cloud-sharpen" colorInterpolationFilters="sRGB">
            <feConvolveMatrix
              order="3"
              preserveAlpha="true"
              kernelMatrix="0 -0.85 0 -0.85 4.4 -0.85 0 -0.85 0"
            />
          </filter>

          {/*
            * Infrared: sharpen first, then key on colour.
            *
            * In that order because the chroma step writes alpha, and
            * convolving afterwards would smear the edges it had just cut.
            */}
          <filter id="cloud-chroma" colorInterpolationFilters="sRGB">
            <feConvolveMatrix
              order="3"
              preserveAlpha="true"
              kernelMatrix="0 -0.85 0 -0.85 4.4 -0.85 0 -0.85 0"
              result="sharp"
            />
            <feColorMatrix
              in="sharp"
              type="matrix"
              values="1 0 0 0 0
                      0 1 0 0 0
                      0 0 1 0 0
                      -2 1 1 0 0"
            />
          </filter>
        </defs>
      </svg>
      <div className="page-header">
        <h1>Map of Systems</h1>
        <p>Monitored systems and the weather clusters they are assigned to</p>
      </div>

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}

      {data && (data.outliers.length > 0 || (data.byState.WA || 0) > 0) && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: 12 }}
          message={`${data.outliers.length + (data.byState.WA || 0)} systems are not plotted`}
          description={(
            <span style={{ fontSize: 12 }}>
              {data.outliers.length > 0 && (
                <>
                  <b>{data.outliers.length}</b> have coordinates outside Australia —{' '}
                  {data.outliers.slice(0, 4).map((o) => o.sn).join(', ')}
                  {data.outliers.length > 4 ? ', …' : ''} — landing in North America and near (0, 0).
                  {(data.outlierClusters || []).length > 0 && (
                    <>
                      {' '}The pipeline has clustered around them, so they have also created{' '}
                      <b>{data.outlierClusters.length}</b> phantom weather stations there
                      ({data.outlierClusters.slice(0, 4).map((c) => c.cluster).join(', ')}
                      {data.outlierClusters.length > 4 ? ', …' : ''}).
                    </>
                  )}
                </>
              )}
              {(data.byState.WA || 0) > 0 && (
                <>
                  {data.outliers.length > 0 ? <br /> : null}
                  <b>{data.byState.WA}</b> in WA, both unassigned to any cluster — no WA view,
                  since it would be an empty map with two orphans on it.
                </>
              )}
            </span>
          )}
        />
      )}

      <Card
        size="small"
        style={{ marginBottom: 12 }}
        styles={{ body: { padding: '8px 12px' } }}
      >
        {/* Filters in one row above the map */}
        <Space wrap size="middle">
          <Segmented
            size="small"
            options={stateOptions}
            value={stateFilter}
            onChange={setStateFilter}
          />
          <Space size={6}>
            <Switch size="small" checked={showLines} onChange={setShowLines} />
            <span style={{ fontSize: 12, color: '#666' }}>links to centre</span>
          </Space>
          <Space size={6}>
            <Switch size="small" checked={showRings} onChange={setShowRings} />
            <span style={{ fontSize: 12, color: '#666' }}>20 km catchment</span>
          </Space>
          <Space size={6}>
            <Switch size="small" checked={showClouds} onChange={setShowClouds} />
            <Tooltip title="Himawari-9 via NASA GIBS, re-imaged every ten minutes. The satellite resolves about a kilometre, so the layer stops sharpening well before the basemap does.">
              <span style={{ fontSize: 12, color: '#666', borderBottom: '1px dotted #ccc' }}>
                cloud
              </span>
            </Tooltip>
            {cloudFrame && (
              <span style={{ fontSize: 11, color: '#999' }}>
                {cloudFrame.at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                {' · '}{cloudFrame.band}
                {cloudFrame.gain > 1 && ` · ×${cloudFrame.gain}`}
              </span>
            )}
          </Space>
          <Space size={4} wrap>
            <span style={{ fontSize: 11, color: '#888' }}>distance to centre:</span>
            {BANDS.map((b) => (
              <Space key={b.label} size={4}>
                <span style={{
                  width: 10, height: 10, borderRadius: '50%', background: b.colour,
                  display: 'inline-block',
                }}
                />
                <span style={{ fontSize: 11, color: '#52514e' }}>{b.label}</span>
              </Space>
            ))}
            <Space size={4} style={{ marginLeft: 6 }}>
              <span style={{
                width: 10, height: 10, borderRadius: '50%', background: UNASSIGNED,
                display: 'inline-block',
              }}
              />
              <span style={{ fontSize: 11, color: '#52514e' }}>unassigned</span>
            </Space>
            <Space size={4} style={{ marginLeft: 6 }}>
              <span style={{
                width: 7, height: 7, borderRadius: '50%', background: CENTRE_INK,
                display: 'inline-block', boxShadow: `0 0 0 1.5px ${SURFACE}`,
              }}
              />
              <span style={{ fontSize: 11, color: '#52514e' }}>weather station</span>
            </Space>
          </Space>
        </Space>
      </Card>

      <Card
        size="small"
        styles={{ body: { padding: 0 } }}
        title={(
          <Space size="small">
            <span>Cluster map</span>
            {data && !selected && (
              <span style={{ color: '#999', fontSize: 11, fontWeight: 400 }}>
                {visible.points.length.toLocaleString()} systems · {clustersInView.length} clusters
                {data.generatedAt ? ` · built ${data.generatedAt}` : ''}
                <span style={{ marginLeft: 8 }}>· click any site or station to isolate its cluster</span>
              </span>
            )}
            {data && selected && (() => {
              const c = clustersInView.find((x) => x.cluster === selected);
              return (
                <Space size={6}>
                  <Tag color="blue" style={{ margin: 0 }}>{selected}</Tag>
                  <span style={{ color: '#999', fontSize: 11, fontWeight: 400 }}>
                    {c ? `${c.systems.toLocaleString()} systems · within ${data.radiusKm} km · furthest ${c.kmMax} km` : ''}
                  </span>
                  <Button
                    size="small" type="link"
                    style={{ padding: 0, height: 'auto', fontSize: 11 }}
                    onClick={() => setSelected(null)}
                  >
                    show all
                  </Button>
                </Space>
              );
            })()}
          </Space>
        )}
        extra={(
          <Space size="small">
            <Tooltip title="Re-read the artefact from Cloud Storage rather than the 30-minute cache">
              <Button size="small" icon={<ReloadOutlined />} loading={loading} onClick={() => load(true)}>
                refresh
              </Button>
            </Tooltip>
          </Space>
        )}
      >
        <Tabs
          size="small"
          tabBarStyle={{ padding: '0 12px', margin: 0 }}
          items={[
            {
              key: 'map',
              label: 'Map',
              children: (
                <div style={{ position: 'relative' }}>
                  <div
                    ref={containerRef}
                    style={{ height: 'calc(100vh - 400px)', minHeight: 400, background: '#eef1f4' }}
                  />
                  {loading && (
                    <div style={{
                      position: 'absolute', inset: 0, display: 'flex', gap: 10,
                      alignItems: 'center', justifyContent: 'center',
                      background: 'rgba(255,255,255,0.9)',
                    }}
                    >
                      <Spin />
                      <span style={{ color: '#888', fontSize: 12 }}>Reading the cluster data…</span>
                    </div>
                  )}
                </div>
              ),
            },
            {
              key: 'table',
              // The table is not an afterthought: two of the mark colours sit
              // below 3:1 on this surface, and a table is how that is made good.
              label: 'Clusters',
              children: (
                <Table
                  size="small"
                  rowKey="cluster"
                  dataSource={clustersInView}
                  pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
                  scroll={{ y: 'calc(100vh - 470px)' }}
                  columns={[
                    { title: 'Cluster', dataIndex: 'cluster', width: 200 },
                    {
                      title: 'State', dataIndex: 'state', width: 80,
                      render: (v) => <Tag>{v}</Tag>,
                    },
                    {
                      title: 'Systems', dataIndex: 'systems', width: 110, align: 'right',
                      sorter: (a, b) => a.systems - b.systems,
                      defaultSortOrder: 'descend',
                      render: (v) => v.toLocaleString(),
                    },
                    {
                      title: 'Mean distance', dataIndex: 'kmMean', width: 140, align: 'right',
                      sorter: (a, b) => a.kmMean - b.kmMean,
                      render: (v, r) => (
                        <Space size={6}>
                          <span style={{
                            width: 9, height: 9, borderRadius: '50%',
                            background: bandOf(r.kmMean).colour, display: 'inline-block',
                          }}
                          />
                          <span>{v} km</span>
                        </Space>
                      ),
                    },
                    {
                      title: 'Furthest', dataIndex: 'kmMax', width: 120, align: 'right',
                      sorter: (a, b) => a.kmMax - b.kmMax,
                      render: (v) => `${v} km`,
                    },
                  ]}
                />
              ),
            },
          ]}
        />
      </Card>

      {/* The dot spinner the Leaflet popup uses. Defined here because the popup
          is an HTML string with no component to render a <Spin> into. */}
      <style>
        {`@keyframes mos-spin-kf { to { transform: rotate(360deg) } }
          .mos-spin { display:inline-block; width:11px; height:11px; margin-right:5px;
            border:2px solid #d9d9d9; border-top-color:#2a78d6; border-radius:50%;
            animation: mos-spin-kf .7s linear infinite; vertical-align:-1px; }`}
      </style>

      <Drawer
        title={detail ? (
          <Space size="small" wrap>
            <span>{detailFor}</span>
            <Tag color="blue">{TZ_LABEL}</Tag>
          </Space>
        ) : detailFor}
        placement="right"
        width={720}
        open={!!detailFor}
        onClose={() => setDetailFor(null)}
        styles={{ body: { paddingTop: 12 } }}
      >
        {now && now.error ? (
          <Alert type="error" showIcon message={now.error} style={{ marginBottom: 12 }} />
        ) : null}
        {(
          <>
            {/* The timezone is stated once, prominently, because the three
                series arrive on three different clocks and the whole panel is
                only comparable once they are on one. */}
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={(
                <span style={{ fontSize: 12 }}>
                  All times <b>{TZ_LABEL}</b> — AEMO settlement time, no daylight saving.
                  {tzOf(now, detail)
                    ? ` Weather converted from the station's own zone (${tzOf(now, detail)}).`
                    : ''}
                </span>
              )}
            />

            <Row gutter={[12, 12]} style={{ marginBottom: 4 }}>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Temperature" value={fmtTemp(nowCur.tempC)}
                  valueStyle={{ fontSize: 20, color: TEMP_INK }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Tooltip title="Global horizontal irradiance for the hour now in progress. No endpoint publishes a measured figure, so this is the forecast for this hour — which is also why it is not in the popup: it costs the day's forecast read.">
                  <Statistic
                    title="Irradiance (this hour)"
                    value={detail && detail.loading ? '…' : fmtGhi(detail?.data?.ghiThisHour)}
                    valueStyle={{ fontSize: 20, color: GHI_INK }}
                  />
                </Tooltip>
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title={nowPrice.region ? `Spot ${nowPrice.region}` : 'Spot price'}
                  value={nowPrice.region
                    ? fmtPrice(nowPrice.latest ? nowPrice.latest.rrp : null)
                    : 'n/a'}
                  valueStyle={{ fontSize: 20, color: PRICE_INK }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Conditions"
                  value={nowCur.condition ? nowCur.condition.text : '—'}
                  valueStyle={{ fontSize: 15 }}
                />
              </Col>
            </Row>

            {(detail?.data?.warnings || []).map((w) => (
              <Alert key={w} type="warning" showIcon style={{ marginBottom: 8 }}
                message={<span style={{ fontSize: 12 }}>{w}</span>} />
            ))}
            {detail && detail.error && (
              <Alert type="error" showIcon style={{ marginBottom: 8 }} message={detail.error} />
            )}
            {detail && detail.loading && (
              <div style={{ padding: '28px 0', textAlign: 'center', color: '#888' }}>
                <Spin size="small" />
                <span style={{ marginLeft: 8, fontSize: 12 }}>reading today&apos;s temperature, irradiance and price…</span>
              </div>
            )}

            {/**
              * Three charts, not one with three axes.
              *
              * Degrees, watts per square metre and dollars per megawatt hour
              * share no scale, and stacking them on one pair of axes makes the
              * two losing series unreadable while implying the crossings mean
              * something. Small multiples on a common x axis compare the shapes
              * honestly.
              *
              * Solid is what happened, dashed is forecast. The dashed series
              * repeats the last past point so the two meet rather than leaving
              * a gap at "now".
              */}
            {detail && detail.data && (
            <>
            <DayChart
              title="Temperature"
              unit="°C"
              colour={TEMP_INK}
              rows={chartRows.temp}
              nowAest={detail.data.nowAest}
              note="Solid: measured. Dashed: forecast for the rest of today."
            />
            <DayChart
              title="Irradiance (GHI)"
              unit="W/m²"
              colour={GHI_INK}
              rows={chartRows.ghi}
              nowAest={detail.data.nowAest}
              note="Modelled throughout — the API publishes no measured irradiance. Solid is the part of today already elapsed."
            />
            {detail.data.price.region ? (
              <DayChart
                title={`Spot price · ${detail.data.price.region}`}
                unit="$/MWh"
                colour={PRICE_INK}
                rows={chartRows.price}
                nowAest={detail.data.nowAest}
                // Asks the payload, not the chart: the last settled interval can
                // land exactly on the current minute, which gives that point a
                // `future` value without any forecast existing.
                note={(detail.data.price.rows || []).some((r) => r.rrpForecast != null)
                  ? 'Settled 5-minute prices, solid. Forecast dashed.'
                  : 'Settled 5-minute prices. No forecast published yet — when one is, it continues as a dashed line.'}
              />
            ) : (
              <Card size="small" style={{ marginTop: 12 }}>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={`${detail.data.station.state || 'This state'} is outside the NEM, so there is no spot price to show`}
                />
              </Card>
            )}
            </>
            )}
          </>
        )}
      </Drawer>
    </PageLayout>
  );
}

/**
 * One measure across today, past solid and future dashed.
 *
 * `past` and `future` are separate keys on the same rows rather than one key
 * with a style switch, because a line cannot change dash pattern midway — two
 * series overlapping at a single shared point is what makes the join seamless.
 */
function DayChart({ title, unit, colour, rows, nowAest, note }) {
  // Whether a dashed half is drawn is decided by the data, not by the caller.
  // Price has none today and will have one later; nothing here needs to change
  // when it arrives.
  const hasFuture = rows.some((r) => r.future != null);
  const nowMins = minsOf(nowAest);
  return (
    <Card size="small" style={{ marginTop: 12 }} styles={{ body: { padding: '10px 8px 4px' } }}>
      <div style={{ padding: '0 8px 6px', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
        <span style={{ fontWeight: 600, fontSize: 13 }}>
          {title} <span style={{ color: '#888', fontWeight: 400, fontSize: 11 }}>{unit}</span>
        </span>
        <span style={{ color: '#888', fontSize: 11 }}>{note}</span>
      </div>
      <div style={{ height: 150 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="#eee" strokeDasharray="2 4" vertical={false} />
            {/* Fixed 0–24 h whatever the data covers, so the three charts read
                as one picture and a gap stays visibly a gap. */}
            <XAxis
              dataKey="m"
              type="number"
              domain={[0, DAY_MINUTES]}
              ticks={AXIS_TICKS}
              tickFormatter={minsLabel}
              tick={{ fontSize: 10, fill: '#888' }}
              tickLine={false}
              axisLine={{ stroke: '#e8e8e8' }}
            />
            <YAxis
              tick={{ fontSize: 10, fill: '#888' }} width={46}
              tickLine={false} axisLine={false}
            />
            <ReTooltip
              formatter={(v, k) => [unit === '$/MWh' ? fmtPrice(v) : `${v} ${unit}`, k === 'future' ? 'forecast' : 'actual']}
              labelFormatter={(m) => `${minsLabel(m)} ${TZ_LABEL}`}
              contentStyle={{ fontSize: 12 }}
            />
            {hasFuture && <Legend wrapperStyle={{ fontSize: 11 }} />}
            {/* Where the record stops and the forecast starts. */}
            {/* A numeric axis marks any instant, so "now" lands in the same
                place on all three charts rather than only where a category
                happened to exist. */}
            {nowMins != null && (
              <ReferenceLine
                x={nowMins} stroke="#bbb" strokeDasharray="3 3"
                label={{ value: 'now', fontSize: 10, fill: '#999', position: 'insideTopRight' }}
              />
            )}
            <Line
              type="monotone" dataKey="past" name="actual" stroke={colour} strokeWidth={2}
              dot={false} connectNulls={false} isAnimationActive={false}
            />
            {hasFuture && (
              <Line
                type="monotone" dataKey="future" name="forecast" stroke={colour} strokeWidth={2}
                strokeDasharray="5 4" dot={false} connectNulls={false} isAnimationActive={false}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}
